/**
 * Manual del Administrador.
 * Verified behavior, derived from code. Alcance deliberado: se documenta lo
 * ADMIN-EXCLUSIVO; para las acciones de módulo (someter/aprobar/editar) se remite a
 * los manuales de cada rol en vez de duplicarlos.
 *  - Acceso completo: el admin pasa todos los gates de rol (isAdmin → true en
 *    receiptController, nominaController isEditableByUser 284, etc.).
 *  - Estructura UI (AdminPortal): /admin = Dashboards (sub-tabs Gastos/Vacaciones/Nómina);
 *    /admin/recibos = "Todos los Recibos"; /admin/tablas = 9 sub-tabs
 *    (Usuarios, Empleados, Proyectos, Tareas, Códigos, Códigos GL, Proveedores Sage,
 *    Feriados, Límites PTO); /admin/configuracion = Ajustes; /admin/auditoria = Auditoría;
 *    /admin/proyecto/:id = configuración de proyecto + ganga.
 *  - Usuarios (UserManagement): roles múltiples por checkbox, manager_id ("Gerente para
 *    PTO L2"), reset de contraseña, activar/desactivar (adminRoutes 31-34).
 *  - Ajustes (AdminSettings): aprobadores PTO L3/L4/L5, recordatorios PTO, O365 SMTP,
 *    número inicial de ticket Sage (adminRoutes settings 56-57). NO exponer secretos.
 *  - Proveedores Sage (SageVendorManagement): Agregar + activar/desactivar; la semilla
 *    (seed) NO es de cara al usuario.
 *  - Proyectos (ProjectEditPage): approval_type 2/3 niveles, ganga única (liderazgo +
 *    trabajadores), tareas/códigos del proyecto.
 *  - Vacaciones EXCLUIDO del alcance (módulo bloqueado para desarrollo posterior).
 * Contenido derivado de: AdminPortal, UserManagement, AdminSettings,
 * SageVendorManagement, ProjectEditPage, adminRoutes, nominaController.
 */
export default {
  roleKey: 'admin',
  roleLabel: 'Administrador',
  icon: '⚙️',
  subtitle: 'Administración completa de la plataforma: usuarios, datos maestros, configuración y supervisión.',
  sections: [
    {
      id: 'vision-general',
      group: 'General',
      title: 'Acceso de administrador — vista general',
      blocks: [
        { type: 'paragraph', text: 'Como administrador usted tiene acceso completo a toda la plataforma. Puede hacer todo lo que hace cualquier otro rol en Gastos y Nómina, además de la administración exclusiva (usuarios, datos maestros, configuración y auditoría).' },
        { type: 'note', variant: 'info', text: 'Para los flujos de cada módulo (someter un gasto, aprobar en cada nivel, crear o editar tarjetas), consulte el manual del rol correspondiente — Chofer, Supervisor(a), Ingeniero(a), Gerente o Revisión Final. Este manual se enfoca en lo que solo el administrador puede hacer.' },
        { type: 'paragraph', text: 'En el menú lateral usted ve: Dashboard, Gastos, Horas, "Sage Report" (exportación, igual que Contabilidad) y Configuración (Tablas, Ajustes y Auditoría).' },
      ],
    },
    {
      id: 'dashboard',
      group: 'General',
      title: 'Dashboards y estadísticas',
      blocks: [
        { type: 'paragraph', text: 'El Dashboard (módulo de inicio) reúne las estadísticas y gráficos de la plataforma, organizados en pestañas: "Gastos" y "Nómina" (KPIs, totales y desgloses por proyecto y por persona).' },
        { type: 'note', variant: 'info', text: 'La exportación a Sage 50 (Gastos y Nómina) funciona igual que para Contabilidad: ábrala desde "Sage Report" y siga los pasos del manual de Contabilidad.' },
      ],
    },
    {
      id: 'acciones-modulo',
      group: 'Gastos y Nómina',
      title: 'Actuar en Gastos y Nómina como administrador',
      blocks: [
        { type: 'paragraph', text: 'Usted puede intervenir en cualquier punto del flujo, sin las restricciones de rol o de proyecto que aplican a los demás.' },
        { type: 'list', items: [
          'Gastos: en "Todos los Recibos" abra cualquier recibo, en cualquier estado y de cualquier proyecto, para revisarlo, corregirlo o aprobarlo.',
          'Nómina: puede aprobar en cualquier nivel y editar las tarjetas en cualquier estado, incluso después de aprobadas.',
        ] },
        { type: 'note', variant: 'warning', text: 'Editar registros ya aprobados es una capacidad exclusiva del administrador y debe usarse con cuidado: úsela solo para correcciones, ya que altera datos que pueden haberse exportado a Sage.' },
        { type: 'note', variant: 'info', text: 'Los pasos de cada acción (revisar, aprobar, devolver, editar) son los mismos que se describen en los manuales de Ingeniero(a), Gerente y Revisión Final.' },
      ],
    },
    {
      id: 'usuarios',
      group: 'Administración',
      title: 'Usuarios y roles',
      blocks: [
        { type: 'paragraph', text: 'En Configuración → Tablas → "Usuarios" gestiona las cuentas de la plataforma.' },
        { type: 'steps', items: [
          'Pulse "Crear Usuario" e ingrese nombre, correo y contraseña inicial.',
          'Asigne uno o varios Roles con las casillas: Chofer, Supervisor(a), Ingeniero(a), Gerente, Revisión Final, Contabilidad y/o Administrador. Un usuario puede tener varios roles a la vez.',
          'Si aplica, asigne el "Gerente (para PTO L2)" — es el supervisor directo de esa persona para las aprobaciones de PTO.',
          'En "Empleado vinculado", busque y elija su ficha de empleado. Si no aparece, créela primero en Tablas → "Empleados".',
          'Guarde.',
        ] },
        { type: 'note', variant: 'info', text: 'Empleado vinculado: es como el portal sabe cuáles son las horas, los recibos y las vacaciones propios de esa cuenta (imprescindible para el rol Oficina). Una cuenta, un empleado: un empleado que ya es de otra cuenta aparece deshabilitado con el nombre de esa cuenta; desvincúlelo allí primero. El vínculo no da permiso para aprobar nada. La columna "Empleado" de la tabla muestra el vínculo actual de cada cuenta.' },
        { type: 'list', items: [
          'Editar: abra un usuario para cambiar sus datos, sus roles o su gerente.',
          'Restablecer contraseña: use "Resetear" para asignar una nueva contraseña.',
          'Activar / Desactivar: desactive una cuenta para retirarle el acceso sin borrarla (puede filtrar por Activos / Inactivos).',
        ] },
        { type: 'note', variant: 'info', text: 'Los roles determinan qué ve y qué puede hacer cada persona. Asigne solo los roles necesarios.' },
      ],
    },
    {
      id: 'empleados',
      group: 'Administración',
      title: 'Empleados (Personas)',
      blocks: [
        { type: 'paragraph', text: 'En Tablas → "Empleados" administra el listado de personas (la fuerza laboral) que aparece en las tarjetas de Nómina. Aquí crea, edita y activa o desactiva empleados.' },
        { type: 'note', variant: 'info', text: 'Las "Personas" (empleados/trabajadores) son distintas de los "Usuarios" con cuenta de acceso: un trabajador de Nómina no necesita una cuenta de usuario para aparecer en las tarjetas. Si un empleado necesita entrar al portal, cree su cuenta en "Usuarios" y vincúlela allí, en "Empleado vinculado".' },
        { type: 'note', variant: 'info', text: 'Carga Masiva: para crear muchos empleados a la vez, pulse "Carga Masiva de Empleados", descargue la plantilla CSV, complétela y súbala. Use siempre la plantilla provista — un formato distinto es la causa más común de error.' },
      ],
    },
    {
      id: 'proyectos-gangas',
      group: 'Administración',
      title: 'Proyectos y Gangas',
      blocks: [
        { type: 'paragraph', text: 'En Tablas → "Proyectos" crea y edita los proyectos. Al abrir un proyecto configura su flujo de aprobación y su equipo (ganga).' },
        { type: 'list', items: [
          'Tipo de aprobación: 2 niveles (Ingeniero(a) → Revisión Final) o 3 niveles (Ingeniero(a) → Gerente → Revisión Final).',
          'Ganga (equipo del proyecto): defina el liderazgo — Supervisor(a), Ingeniero(a) y, en proyectos de 3 niveles, Gerente — y los trabajadores. El sistema resuelve los aprobadores de Nómina a partir de la ganga.',
          'Tareas y códigos del proyecto: vincule las tareas (cost codes) disponibles para ese proyecto.',
        ] },
        { type: 'note', variant: 'info', text: 'En proyectos de 3 niveles, el Gerente es obligatorio en la ganga. En proyectos de 2 niveles no hay paso de Gerente.' },
        { type: 'note', variant: 'info', text: 'Carga Masiva: para crear muchos proyectos a la vez, pulse "Carga Masiva de Proyectos", descargue la plantilla CSV, complétela y súbala. Use siempre la plantilla provista — un formato distinto es la causa más común de error.' },
      ],
    },
    {
      id: 'tablas',
      group: 'Administración',
      title: 'Tablas de referencia',
      blocks: [
        { type: 'paragraph', text: 'En Configuración → Tablas administra los datos maestros que alimentan todo el sistema. Cada uno es una pestaña con crear, editar y eliminar/activar:' },
        { type: 'list', items: [
          'Tareas: los cost codes de trabajo que se asignan a las partidas y a las horas.',
          'Códigos: los centros de costo.',
          'Códigos GL: las cuentas contables (GL) para la codificación administrativa.',
          'Feriados: el calendario de días feriados (puede copiarlos a otro año y activarlos o desactivarlos).',
          'Límites PTO: los topes de PTO por rol.',
        ] },
        { type: 'note', variant: 'info', text: 'Estos datos se usan en toda la plataforma, así que un cambio aquí afecta a todos los módulos. Revise bien antes de eliminar un registro en uso.' },
        { type: 'note', variant: 'info', text: 'Carga Masiva (solo Tareas): en la pestaña "Tareas", pulse "Carga Masiva de Tareas", descargue la plantilla CSV, complétela y súbala para crear muchas tareas a la vez. Use siempre la plantilla provista — un formato distinto es la causa más común de error.' },
      ],
    },
    {
      id: 'proveedores-sage',
      group: 'Administración',
      title: 'Proveedores Sage',
      blocks: [
        { type: 'paragraph', text: 'En Tablas → "Proveedores Sage" administra el catálogo de proveedores que se usa para asignar el Proveedor Sage a los recibos antes de exportarlos.' },
        { type: 'steps', items: [
          'Pulse "Agregar Proveedor".',
          'Ingrese el Vendor ID (p. ej., HOMEDEPOT), el Nombre del Proveedor y la Categoría (Credit Card o Petty Cash).',
          'Pulse "Agregar".',
        ] },
        { type: 'list', items: [
          'Active o desactive un proveedor desde la columna "Estado" (puede filtrar por Activos / Inactivos).',
          'Use el buscador por Vendor ID o nombre, y el filtro por categoría.',
        ] },
        { type: 'note', variant: 'info', text: 'El catálogo se administra desde esta pantalla. Mantenga activos solo los proveedores vigentes para que la asignación en los recibos sea clara.' },
      ],
    },
    {
      id: 'ajustes',
      group: 'Administración',
      title: 'Ajustes de la plataforma',
      blocks: [
        { type: 'paragraph', text: 'En Configuración → Ajustes ("Configuración de la Plataforma") administra los parámetros del sistema. Edite los valores y pulse "Guardar".' },
        { type: 'list', items: [
          'Aprobadores de PTO: los correos de los aprobadores de niveles superiores (L3 Administración, L4 Recursos Humanos, L5 Directora).',
          'Recordatorios de PTO: los umbrales de horas para enviar recordatorios.',
          'Correo O365 (SMTP): servidor, puerto, usuario, contraseña y nombre del remitente para el envío de notificaciones.',
          'Número inicial de Ticket: el punto de partida de la numeración de tickets para la exportación de Nómina a Sage.',
        ] },
        { type: 'note', variant: 'warning', text: 'Las credenciales de correo (contraseña O365) son sensibles. No las comparta ni las divulgue fuera de este panel.' },
      ],
    },
    {
      id: 'auditoria',
      group: 'Administración',
      title: 'Auditoría',
      blocks: [
        { type: 'paragraph', text: 'En Configuración → Auditoría consulta el registro de auditoría: el historial de acciones realizadas en la plataforma.' },
        { type: 'steps', items: [
          'Abra "Auditoría".',
          'Filtre por tipo de entidad, tipo de evento, usuario que actuó o rango de fechas.',
          'Revise los eventos en orden cronológico (del más reciente al más antiguo).',
        ] },
        { type: 'note', variant: 'info', text: 'Use la auditoría para verificar quién hizo qué y cuándo, especialmente al investigar una corrección o un cambio inesperado.' },
      ],
    },
  ],
};
